<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Table from '../../Table.svelte';
  import SearchInput from '../../SearchInput.svelte';
  import Pagination from '../../Pagination.svelte';
  import LoadingSkeleton from '../../LoadingSkeleton.svelte';
  import EmptyState from '../../EmptyState.svelte';
  import ErrorState from '../../ErrorState.svelte';
  import {
    adminDashboardService,
    type RecentPendingPayout
  } from '../../../lib/api/admin/dashboard.service';
  import { createPreferencesManager } from '../../../lib/utils/preferences';
  import { formatDate } from '../../../lib/utils/formatters';

  const preferences = createPreferencesManager('pending-payouts');

  let loading = true;
  let error: string | null = null;
  let payouts: RecentPendingPayout[] = [];
  let totalItems = 0;
  let totalPages = 1;

  const savedFilters = preferences.getFilters();
  let searchQuery = savedFilters.searchQuery || '';
  let currentPage = preferences.getCurrentPage() || 1;
  const itemsPerPage = 20;

  const tableColumns = [
    {
      key: 'employer_name',
      label: 'Employer',
      width: '28%',
      mobilePrimary: true,
      render: (value: string) => `<span class="employer-name">${value}</span>`
    },
    {
      key: 'charity_name',
      label: 'Charity',
      width: '28%',
      render: (value: string) => `<span class="charity-name">${value}</span>`
    },
    {
      key: 'amount',
      label: 'Amount',
      width: '22%',
      render: (value: number) =>
        `<span class="amount-value">${formatCurrency(value)}</span>`
    },
    {
      key: 'payment_date',
      label: 'Employer paid',
      width: '22%',
      render: (value: string | null) =>
        `<span class="date-value">${value ? formatDate(value) : '—'}</span>`
    }
  ];

  function formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  async function loadPayouts() {
    loading = true;
    error = null;

    try {
      const response = await adminDashboardService.getPendingPayouts({
        page: currentPage,
        per_page: itemsPerPage,
        phrase: searchQuery || undefined
      });

      if (response.success && response.data) {
        payouts = response.data.items;
        totalItems = response.data.paging.total;
        totalPages = response.data.paging.last_page;
      } else {
        error = response.message || 'Failed to load pending payouts';
      }
    } catch (err: unknown) {
      error = err instanceof Error ? err.message : 'Failed to load pending payouts';
    } finally {
      loading = false;
    }
  }

  function handleSearch(event: CustomEvent<string>) {
    searchQuery = event.detail;
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { searchQuery } });
    loadPayouts();
  }

  function handlePageChange(event: CustomEvent<{ page: number }>) {
    currentPage = event.detail.page;
    preferences.update({ currentPage });
    loadPayouts();
  }

  function handleRowClick(row: RecentPendingPayout) {
    if (row?.employer_payment_uuid) {
      push(`/admin/payments/received/${row.employer_payment_uuid}`);
    }
  }

  function clearSearch() {
    searchQuery = '';
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { searchQuery: '' } });
    loadPayouts();
  }

  onMount(() => {
    loadPayouts();
  });
</script>

<div class="payments-list-page">
  <div class="page-header">
    <p class="subtitle">Employer payments received that still need to be paid out to charities. Open a row to mark the payout as paid.</p>
  </div>

  <div class="filters-section">
    <SearchInput
      placeholder="Search by employer or charity..."
      bind:value={searchQuery}
      on:search={handleSearch}
      width="100%"
    />
  </div>

  <div class="table-section">
    {#if loading}
      <div class="loading-wrap">
        <LoadingSkeleton lines={8} height="2.5rem" />
      </div>
    {:else if error}
      <ErrorState message="Unable to load pending payouts" description={error} onRetry={loadPayouts} />
    {:else if payouts.length === 0}
      <EmptyState
        message="No pending payouts"
        description={searchQuery
          ? 'Try adjusting your search to see more results.'
          : 'All employer payments have been paid out to charities.'}
        actionLabel={searchQuery ? 'Clear search' : null}
        onAction={searchQuery ? clearSearch : null}
      />
    {:else}
      <Table columns={tableColumns} data={payouts} onRowClick={handleRowClick} />
      {#if totalPages > 1}
        <div class="pagination-container">
          <Pagination {currentPage} {totalPages} on:pageChange={handlePageChange} />
        </div>
      {/if}
    {/if}
  </div>
</div>

<style>
  .page-header {
    margin-bottom: 1.5rem;
  }

  .subtitle {
    margin: 0;
    font-size: 0.875rem;
    color: #6b7280;
    line-height: 1.5;
  }

  .filters-section {
    margin-bottom: 1rem;
    width: 100%;
  }

  .table-section {
    background: white;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    overflow: hidden;
  }

  .loading-wrap {
    padding: 2rem;
    min-height: 320px;
  }

  .pagination-container {
    padding: 1rem 1.5rem;
    border-top: 1px solid #f3f4f6;
  }

  :global(.amount-value) {
    font-weight: 600;
    color: #111827;
  }
</style>
